import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import {
  WebsiteChatWidgetView,
  type WidgetMessage,
} from "@/components/widget/website-chat-widget-view"

import type { ChatbotSetupData } from "./types"

// IMP-429: the setup Preview renders the SAME widget visitors see
// (WebsiteChatWidgetView), driven live by the form config, so appearance and
// layout stay in sync with the published widget by construction. Replies are
// stubbed locally — the surface is the real widget, but preview messages never
// open a real conversation, hit production infrastructure, or persist.
const PREVIEW_REPLY =
  "Thanks for the message! This is a preview, so I'm not wired to your knowledge base yet — but this is exactly how your assistant's replies will look to visitors."

// How long the typing indicator shows before the stubbed reply lands.
const REPLY_DELAY_MS = 900

function PreviewWidget({ data }: { data: ChatbotSetupData }) {
  // Open by default so the configuration is visible and interactive at a glance.
  const [open, setOpen] = useState(true)
  const [messages, setMessages] = useState<WidgetMessage[]>([])
  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  // IMP-454: closing the preview widget shows the pre-open popup when the
  // toggle is on, mirroring the live widget. Dismissing hides it like the
  // one-time live nudge; flipping the toggle brings it back so the setting's
  // effect is always visible.
  const [teaserDismissed, setTeaserDismissed] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTeaserDismissed(false)
  }, [data.popup_enabled])
  const replyTimer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (replyTimer.current) window.clearTimeout(replyTimer.current)
    },
    [],
  )

  // Mirrors the live widget's send (visitor bubble → typing indicator → reply),
  // but the reply is a local stub rather than a channel round-trip.
  function send(text?: string) {
    const trimmed = (text ?? input).trim()
    if (!trimmed || sending) return

    setMessages((prev) => [...prev, { role: "visitor", text: trimmed }])
    setInput("")
    setSending(true)
    replyTimer.current = window.setTimeout(() => {
      setSending(false)
      setMessages((prev) => [...prev, { role: "bot", text: PREVIEW_REPLY }])
    }, REPLY_DELAY_MS)
  }

  return (
    <WebsiteChatWidgetView
      config={data}
      messages={messages}
      input={input}
      sending={sending}
      open={open}
      teaser={data.popup_enabled && !teaserDismissed}
      greeting
      onOpenChange={setOpen}
      onInputChange={setInput}
      onSend={send}
      onDismissTeaser={() => setTeaserDismissed(true)}
      positioning="absolute"
    />
  )
}

export function PreviewPanel({
  data,
  onClose,
}: {
  data: ChatbotSetupData
  onClose: () => void
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
        <p className="text-sm font-semibold">Preview</p>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close preview"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="bg-muted/30 flex flex-1 flex-col overflow-hidden">
        <div className="bg-background flex shrink-0 items-center gap-2 border-b px-4 py-2">
          <div className="flex gap-1.5">
            <div className="size-2 rounded-full bg-red-400" />
            <div className="size-2 rounded-full bg-amber-400" />
            <div className="size-2 rounded-full bg-green-400" />
          </div>
          <div className="bg-muted text-muted-foreground mx-auto flex-1 rounded px-3 py-0.5 text-center text-xs">
            {data.embed_domain?.trim() || "yoursite.com"}
          </div>
        </div>

        {/* A faux page behind the widget, positioning it in the corner exactly as
            it sits on a real site. */}
        <div className="relative flex-1 space-y-2.5 overflow-hidden p-5">
          <div className="bg-muted/60 h-3.5 w-1/2 rounded" />
          <div className="bg-muted/40 h-2.5 w-full rounded" />
          <div className="bg-muted/40 h-2.5 w-5/6 rounded" />
          <div className="bg-muted/40 h-2.5 w-4/6 rounded" />
          <div className="bg-muted/30 mt-3 h-20 w-full rounded-lg" />
          <div className="bg-muted/30 h-2.5 w-full rounded" />
          <div className="bg-muted/30 h-2.5 w-3/4 rounded" />

          <PreviewWidget data={data} />
        </div>
      </div>
    </div>
  )
}
